import { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import qs from 'query-string'

import { STORAGE_KEY_VOUCHER_APPLICABLE_PLANS, STORAGE_KEY_SELECTED_PLAN_ON_GUEST } from '../../../storage-keys'
import { usePlanDetails } from '../../shared/hooks/use-plan-details'
import { SIGN_UP_SCREENS } from '../../../constants'
import { standardPlan } from '../../price-table/constants'

/**
 * Manages plan selection logic during the sign-up flow.
 *
 * Handles:
 * - Determining the default selected plan based on various sources (voucher, session storage, pre-selection)
 * - Validating voucher-applicable plans
 * - Navigating to the next sign-up step with the selected plan
 * - Updating the currently active subscription selection
 */

const DEFAULT_PLAN = standardPlan.title

export const useSelectPlan = (currentSubscription, preSelectedPlan) => {
  const history = useHistory()
  const location = useLocation()
  const { voucher } = qs.parse(location.search)
  const { getPlanTitleBySku, getPlanDetailByTitle } = usePlanDetails()

  // Get the pre selected plan if it exists when users access the select plan table
  const prePlan = sessionStorage.getItem(STORAGE_KEY_SELECTED_PLAN_ON_GUEST)
  const planFromGuestHomePage = prePlan && getPlanDetailByTitle(prePlan)?.title

  // Get the voucher applicable plans if voucher exists
  const voucherApplicablePlanSkus = sessionStorage.getItem(STORAGE_KEY_VOUCHER_APPLICABLE_PLANS)?.split(',')
  const voucherApplicablePlanNames = []
  voucher && voucherApplicablePlanSkus?.forEach(sku => {
    voucherApplicablePlanNames.push(getPlanTitleBySku(sku))
  })

  // Set the first voucher applicable plan as default active plan or
  // if planFromGuestHomePage is one of the voucher applicable plans
  // then set planFromGuestHomePage as default active plan
  let voucherRelatedPlan = null
  if (voucherApplicablePlanNames.includes(planFromGuestHomePage)) {
    voucherRelatedPlan = planFromGuestHomePage
  } else {
    voucherRelatedPlan = voucherApplicablePlanNames[0]
  }
  const mapState = useSelector(state => state)
  const { voucherValidateType } = mapState
  const [activeSubscription, setActiveSubscription] = useState(preSelectedPlan || currentSubscription || voucherValidateType || voucherRelatedPlan || planFromGuestHomePage || DEFAULT_PLAN)

  const selectToNext = (type) => {
    history.push({
      pathname: SIGN_UP_SCREENS.ACCOUNT_DETAILS.path,
      search: qs.stringify({
        ...qs.parse(location.search),
        plan: type
      })
    })

    // Remove the pre selected plan when users go to account details page(next sign-up step).
    sessionStorage.removeItem(STORAGE_KEY_SELECTED_PLAN_ON_GUEST)
  }

  const updateActiveSubscription = (data) => {
    setActiveSubscription(data)
  }

  return {
    activeSubscription,
    updateActiveSubscription,
    selectToNext
  }
}
